const MAX_GLOBAL_LIGHTNING = 10000
const MAX_TIMELINE_ITEMS = 500
const MAX_STATION_MESSAGES = 200
const MAX_GLOBAL_SHIPS = 20000

export const GLOBAL_MEMORY_EVENTS = [
  'global:viewers',
  'global:alerts',
  'global:lightning',
  'global:radar',
  'global:satellite',
  'global:ship',
  'global:positions',
]

export const STATION_MEMORY_EVENTS = [
  'station:metadata',
  'station:weather',
  'station:camera',
  'station:auto',
  'station:lightning',
  'station:message',
  'station:presence',
  'station:chat',
  'station:alert',
  'station:export',
]

export const EMPTY_STATION_MEMORY = Object.freeze({
  metadata: null,
  weather: null,
  weatherFieldUpdatedAt: Object.freeze({}),
  camera: null,
  lightning: Object.freeze([]),
  latestLightning: null,
  latestLightningEvent: null,
  messages: Object.freeze([]),
  alerts: Object.freeze([]),
  exportJobs: Object.freeze({}),
  latestExport: null,
  presenceUsers: Object.freeze([]),
  chatEvent: null,
  autoModeData: null,
  lastMessage: null,
  lastEvent: null,
  updatedAt: null,
})

export function createInitialGlobalMemoryState() {
  return {
    connection: {
      status: 'idle',
      connected: false,
      socketId: null,
      upstreamConnected: null,
      error: null,
    },
    hydration: {
      global: { status: 'idle', error: null, refreshedAt: null },
      globalTopics: {},
      stations: {},
    },
    global: {
      viewers: 0,
      alerts: [],
      // Keep arrival source by alert ID so a restored batch cannot trigger live notifications.
      alertEventActions: {},
      lightning: [],
      latestLightning: null,
      latestLightningEvent: null,
      radar: [],
      satellite: null,
      satelliteUpdates: [],
      latestSatellite: null,
      ships: [],
      positions: {},
      lastEvent: null,
      updatedAt: null,
    },
    stations: {},
    catalog: {
      items: null,
      status: 'idle',
      error: null,
      scope: 'all',
      updatedAt: null,
    },
    lastEvent: null,
  }
}

function createStationMemory() {
  return {
    metadata: null,
    weather: null,
    weatherFieldUpdatedAt: {},
    camera: null,
    lightning: [],
    latestLightning: null,
    latestLightningEvent: null,
    messages: [],
    alerts: [],
    exportJobs: {},
    latestExport: null,
    presenceUsers: [],
    chatEvent: null,
    autoModeData: null,
    lastMessage: null,
    lastEvent: null,
    updatedAt: null,
  }
}

function getEventTimestamp(payload) {
  return payload?.timestamp || new Date().toISOString()
}

function withoutEnvelope(payload) {
  if (!payload || typeof payload !== 'object') return payload
  const { stationId: _stationId, timestamp: _timestamp, ...data } = payload
  return data
}

function normalizeStrike(strike) {
  if (Array.isArray(strike)) {
    return strike.slice(0, 3)
  }

  if (!strike || typeof strike !== 'object') return null

  // High-resolution station sensor events carry additional measurements and
  // remain structured. Standard geolocated strikes use the compact tuple.
  if (strike.range || strike.strikeType || strike.distance !== undefined) {
    return strike
  }

  return [strike.lat, strike.lon, strike.timestamp]
}

function itemTimestamp(item) {
  const raw = Array.isArray(item) ? item[2] : item?.timestamp
  const numeric = Number(raw)
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

function newest(items) {
  return items.reduce((latest, item) => (
    !latest || itemTimestamp(item) >= itemTimestamp(latest) ? item : latest
  ), null)
}

function alertKey(alert) {
  if (!alert || typeof alert !== 'object') return JSON.stringify(alert)
  return alert.id || [alert.event, alert.effective, alert.expires, alert.wfo].join('|')
}

function strikeKey(strike) {
  const item = normalizeStrike(strike) || {}
  if (Array.isArray(item)) return item.join('|')
  return [item.timestamp, item.lat, item.lon, item.intensity, item.range, item.strikeType].join('|')
}

function radarKey(item) {
  return typeof item === 'string' ? item : item?.timestamp || JSON.stringify(item)
}

function satelliteKey(item) {
  return [item?.satellite, item?.band, item?.timestamp].join('|')
}

function shipKey(item) {
  return String(item?.id || item?.properties?.mmsi || '')
}

function appendUnique(items, item, getKey, maxItems) {
  if (item === null || item === undefined) return items
  const key = getKey(item)
  return [...items.filter((current) => getKey(current) !== key), item].slice(-maxItems)
}

function removeItem(items, item, getKey) {
  const key = getKey(item)
  return items.filter((current) => getKey(current) !== key)
}

function normalizeCollection(payload, keys = []) {
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function applyCollectionEvent(items, payload, itemName, getKey, maxItems) {
  const item = payload?.[itemName] ?? payload
  const action = payload?.type || payload?.action || 'new'
  if (action === 'remove' || action === 'delete') {
    return removeItem(items, item, getKey)
  }
  return appendUnique(items, item, getKey, maxItems)
}

function mergeCatalogStation(items, stationId, metadata) {
  if (!Array.isArray(items)) return items
  let matched = false
  const next = items.map((station) => {
    const ids = [station?.internalId, station?.id, station?.station_id].map(String)
    if (!ids.includes(String(stationId))) return station
    matched = true
    return { ...station, ...metadata }
  })
  return matched ? next : items
}

function getCatalogMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return {}
  const fields = ['viewers', 'isOnline', 'hasVideo', 'stationPing', 'uptime', 'version', 'node']
  return Object.fromEntries(
    fields.filter((field) => Object.prototype.hasOwnProperty.call(metadata, field))
      .map((field) => [field, metadata[field]])
  )
}

function mergeCatalogWithStationMemory(items, stations) {
  if (!Array.isArray(items)) return items
  return items.map((station) => {
    const ids = [station?.internalId, station?.id, station?.station_id]
      .filter((id) => id !== undefined && id !== null)
      .map(String)
    const memory = ids.map((id) => stations[id]).find(Boolean)
    return memory?.metadata ? { ...station, ...getCatalogMetadata(memory.metadata) } : station
  })
}

function toLegacyStationMessage(eventName, payload) {
  const timestamp = getEventTimestamp(payload)
  if (eventName === 'station:metadata') {
    return {
      type: 'viewers-update',
      stationId: payload.stationId,
      viewers: payload.viewers,
      timestamp,
    }
  }
  if (eventName === 'station:weather') {
    return {
      type: 'station-update',
      stationId: payload.stationId,
      timestamp,
      data: { type: 'weather', data: withoutEnvelope(payload) },
    }
  }
  if (eventName === 'station:camera') {
    return {
      type: 'station-update',
      stationId: payload.stationId,
      timestamp,
      data: { type: 'camera-status', ...withoutEnvelope(payload) },
    }
  }
  if (eventName === 'station:lightning') {
    return {
      type: 'station-update',
      stationId: payload.stationId,
      timestamp,
      data: { type: 'lightning-strike', data: payload.strike || withoutEnvelope(payload) },
    }
  }
  if (eventName === 'station:export') {
    return {
      type: 'station-update',
      stationId: payload.stationId,
      timestamp,
      data: { type: 'export', ...withoutEnvelope(payload) },
    }
  }
  return { type: eventName, ...payload, timestamp }
}

export function createGlobalMemoryStore(initialState = createInitialGlobalMemoryState()) {
  let state = initialState
  const listeners = new Set()

  const publish = (updater) => {
    const next = typeof updater === 'function' ? updater(state) : updater
    if (next === state) return
    state = next
    listeners.forEach((listener) => listener())
  }

  const applyGlobalEvent = (eventName, payload) => {
    publish((current) => {
      const timestamp = getEventTimestamp(payload)
      const event = { name: eventName, payload, receivedAt: new Date().toISOString() }
      let global = { ...current.global, lastEvent: event, updatedAt: timestamp }

      if (eventName === 'global:viewers') {
        global.viewers = Number(payload?.viewers) || 0
      } else if (eventName === 'global:alerts') {
        global.alerts = applyCollectionEvent(global.alerts, payload, 'alert', alertKey, MAX_TIMELINE_ITEMS)
        const key = alertKey(payload?.alert ?? payload)
        const action = payload?.type || payload?.action || 'new'
        global.alertEventActions = Object.fromEntries(global.alerts.map((alert) => {
          const alertId = alertKey(alert)
          return [alertId, alertId === key ? action : current.global.alertEventActions?.[alertId] || 'sync']
        }))
      } else if (eventName === 'global:lightning') {
        const strike = normalizeStrike(payload?.strike || payload)
        global.lightning = applyCollectionEvent(global.lightning, { ...payload, strike }, 'strike', strikeKey, MAX_GLOBAL_LIGHTNING)
        global.latestLightning = newest(global.lightning)
        if (strike && payload?.type !== 'remove' && payload?.type !== 'delete') {
          global.latestLightningEvent = { strike, receivedAt: event.receivedAt }
        }
      } else if (eventName === 'global:radar') {
        global.radar = applyCollectionEvent(global.radar, payload, 'timestamp', radarKey, MAX_TIMELINE_ITEMS)
      } else if (eventName === 'global:satellite') {
        global.satelliteUpdates = applyCollectionEvent(
          global.satelliteUpdates,
          payload,
          'satelliteUpdate',
          satelliteKey,
          MAX_TIMELINE_ITEMS
        )
        global.latestSatellite = payload?.type === 'remove' ? newest(global.satelliteUpdates) : payload
      } else if (eventName === 'global:ship') {
        global.ships = applyCollectionEvent(
          global.ships,
          { ...payload, type: payload?.action || payload?.type },
          'feature',
          shipKey,
          MAX_GLOBAL_SHIPS
        )
      } else if (eventName === 'global:positions') {
        const stationId = String(payload?.stationId || '')
        if (stationId) {
          global.positions = {
            ...(global.positions || {}),
            [stationId]: {
              ...(global.positions?.[stationId] || {}),
              ...payload,
            },
          }
        }
      }

      return { ...current, global, lastEvent: event }
    })
  }

  const applyStationEvent = (eventName, payload) => {
    const stationId = String(payload?.stationId || '')
    if (!stationId) return
    const timestamp = getEventTimestamp(payload)
    const event = { name: eventName, payload, receivedAt: new Date().toISOString() }
    const legacyMessage = toLegacyStationMessage(eventName, payload)

    publish((current) => {
      const previous = current.stations[stationId] || createStationMemory()
      let station = {
        ...previous,
        lastEvent: event,
        lastMessage: legacyMessage,
        updatedAt: timestamp,
      }

      if (eventName === 'station:metadata') {
        station.metadata = { ...(previous.metadata || {}), ...withoutEnvelope(payload) }
      } else if (eventName === 'station:weather') {
        const weatherPatch = withoutEnvelope(payload)
        station.weather = { ...(previous.weather || {}), ...weatherPatch }
        station.weatherFieldUpdatedAt = {
          ...(previous.weatherFieldUpdatedAt || {}),
          ...Object.fromEntries(Object.keys(weatherPatch).map((field) => [field, timestamp])),
        }
      } else if (eventName === 'station:camera') {
        station.camera = { ...(previous.camera || {}), ...withoutEnvelope(payload) }
      } else if (eventName === 'station:auto') {
        station.autoModeData = {
          ...(payload.autoModeData || withoutEnvelope(payload)),
          updatedAt: timestamp,
        }
      } else if (eventName === 'station:lightning') {
        const strike = normalizeStrike(payload.strike || payload)
        station.lightning = applyCollectionEvent(previous.lightning, { ...payload, strike }, 'strike', strikeKey, MAX_TIMELINE_ITEMS)
        station.latestLightning = newest(station.lightning)
        if (strike && payload?.type !== 'remove' && payload?.type !== 'delete') {
          station.latestLightningEvent = { strike, receivedAt: event.receivedAt }
        }
      } else if (eventName === 'station:message') {
        const message = payload.message || payload
        if (payload.type === 'delete' && payload.messageId) {
          station.messages = previous.messages.filter((item) => String(item?.id) !== String(payload.messageId))
        } else {
          station.messages = applyCollectionEvent(previous.messages, payload, 'message', (item) => item?.id || JSON.stringify(item), MAX_STATION_MESSAGES)
        }
        station.lastMessage = { type: 'station-update', stationId, timestamp, data: { type: 'message', data: message } }
      } else if (eventName === 'station:presence') {
        station.presenceUsers = Array.isArray(payload.users) ? payload.users : []
      } else if (eventName === 'station:chat') {
        station.chatEvent = payload
      } else if (eventName === 'station:alert') {
        station.alerts = applyCollectionEvent(previous.alerts, payload, 'alert', alertKey, MAX_TIMELINE_ITEMS)
      } else if (eventName === 'station:export') {
        const exportId = String(payload.exportId || '')
        if (exportId) {
          const previousExportJobs = previous.exportJobs || {}
          station.exportJobs = {
            ...previousExportJobs,
            [exportId]: { ...(previousExportJobs[exportId] || {}), ...withoutEnvelope(payload) },
          }
        }
        station.latestExport = withoutEnvelope(payload)
      }

      const catalogItems = eventName === 'station:metadata'
        ? mergeCatalogStation(current.catalog.items, stationId, getCatalogMetadata(withoutEnvelope(payload)))
        : current.catalog.items

      return {
        ...current,
        stations: { ...current.stations, [stationId]: station },
        catalog: catalogItems === current.catalog.items
          ? current.catalog
          : { ...current.catalog, items: catalogItems },
        lastEvent: event,
      }
    })
  }

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    setConnection(patch) {
      publish((current) => ({
        ...current,
        connection: { ...current.connection, ...patch },
      }))
    },
    setGlobalHydration(patch) {
      publish((current) => ({
        ...current,
        hydration: {
          ...current.hydration,
          global: { ...current.hydration.global, ...patch },
        },
      }))
    },
    setGlobalTopicHydration(topic, patch) {
      publish((current) => ({
        ...current,
        hydration: {
          ...current.hydration,
          global: { ...current.hydration.global, ...patch },
          globalTopics: {
            ...(current.hydration.globalTopics || {}),
            [topic]: {
              status: 'idle',
              error: null,
              refreshedAt: null,
              ...current.hydration.globalTopics?.[topic],
              ...patch,
            },
          },
        },
      }))
    },
    setStationHydration(stationId, patch) {
      publish((current) => ({
        ...current,
        hydration: {
          ...current.hydration,
          stations: {
            ...current.hydration.stations,
            [stationId]: {
              status: 'idle',
              error: null,
              refreshedAt: null,
              ...current.hydration.stations[stationId],
              ...patch,
            },
          },
        },
      }))
    },
    hydrateGlobalDomain(domain, payload) {
      publish((current) => {
        const global = { ...current.global }
        if (domain === 'alerts') {
          global.alerts = normalizeCollection(payload, ['alerts'])
          global.alertEventActions = Object.fromEntries(global.alerts.map((alert) => [alertKey(alert), 'sync']))
        }
        if (domain === 'lightning') {
          global.lightning = normalizeCollection(payload, ['lightning', 'strikes']).map(normalizeStrike).filter(Boolean)
          global.latestLightning = newest(global.lightning)
        }
        if (domain === 'radar') global.radar = normalizeCollection(payload, ['radar', 'timestamps'])
        if (domain === 'satellite') global.satellite = payload
        return { ...current, global }
      })
    },
    hydrateStation(stationId, { metadata, weather }) {
      const id = String(stationId)
      publish((current) => {
        const previous = current.stations[id] || createStationMemory()
        const station = { ...previous }
        if (metadata) {
          station.metadata = { ...(previous.metadata || {}), ...metadata }
          station.camera = metadata.cameraData || metadata.camera || (
            metadata.bearing !== undefined || metadata.tilt !== undefined
              ? { bearing: metadata.bearing, tilt: metadata.tilt }
              : previous.camera
          )
          station.messages = metadata.messages || (metadata.message ? [metadata.message] : previous.messages)
          station.alerts = metadata.alerts || previous.alerts
        }
        if (weather) {
          station.weather = { ...(previous.weather || {}), ...weather }
          station.weatherFieldUpdatedAt = {
            ...(previous.weatherFieldUpdatedAt || {}),
            ...Object.fromEntries(
              Object.keys(weather).map((field) => [
                field,
                weather[`${field}_timestamp`] ?? weather.timestamp ?? null,
              ])
            ),
          }
        }
        station.updatedAt = new Date().toISOString()

        return {
          ...current,
          stations: { ...current.stations, [id]: station },
          catalog: current.catalog,
        }
      })
    },
    applyEvent(eventName, payload = {}) {
      if (GLOBAL_MEMORY_EVENTS.includes(eventName)) applyGlobalEvent(eventName, payload)
      if (STATION_MEMORY_EVENTS.includes(eventName)) applyStationEvent(eventName, payload)
    },
    setCatalog(patch) {
      publish((current) => {
        const nextPatch = Object.prototype.hasOwnProperty.call(patch, 'items')
          ? { ...patch, items: mergeCatalogWithStationMemory(patch.items, current.stations) }
          : patch
        return {
          ...current,
          catalog: { ...current.catalog, ...nextPatch },
        }
      })
    },
  }
}
